import * as Location from 'expo-location';
import { clone } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Dimensions, Image, Keyboard, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import { geocode } from '../../utils/geocoding';
import { logAnalyticsEvent } from '../../utils/logAnalyticsEvent';
import { log } from '../../utils/logger';
import pantryRegionForUser from '../../utils/pantryRegionForUser';
import timeUtils from '../../utils/timeUtils';
import LazyMemoizedPantryMarker from '../map/LazyMemoizedPantryMarker';
import { MapView as MapViewImpl, makeMapViewRegion } from '../map/MapView';
import PantryDistance from './PantryDistance';
import PantryPhoto from './PantryPhoto';
import { getMapMarkerIcon } from './pantryIcon';

import { AppText } from '../AppText';

const styles = StyleSheet.create({
  container: {
    padding: 0,
    flex: 1,
    position: 'relative',
  },
  mapStyle: {
    flex: 1,
  },
  pantryDetailsContainer: {
    position: 'absolute',
    zIndex: 1,
    bottom: 0,
    left: 0,
    right: 0,
    height: 116,
    backgroundColor: Colors.white,
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 13,
    paddingBottom: 13,
    paddingLeft: 13,
    flex: 1,
  },
  spinnerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pantryDetailsTextContainer: {
    paddingHorizontal: 16,
    width: Layout.fullWidth - 96,
  },
  pantryDetailsName: {
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
    color: Colors.veryDarkGray,
    fontSize: 16,
    fontWeight: '600',
  },
  pantryDistance: {
    color: Colors.veryDarkGray,
    marginTop: 4,
  },
  nextService: {
    fontFamily: 'roboto-regular',
    color: Colors.mediumBlue,
    fontSize: 12,
    lineHeight: 20,
    marginTop: 8,
  },
  link: {
    fontFamily: 'roboto-regular',
    color: Colors.primaryColor,
    fontSize: 12,
    fontWeight: '600',
    marginTop: 8,
    textDecorationLine: 'underline',
    textTransform: 'uppercase',
  },
  callOut: {
    display: 'none',
  },
  locationCross: {
    width: 56,
    height: 56,
    position: 'absolute',
    bottom: 36,
    left: 16,
  },
  zoomHelperContainer: {
    position: 'absolute',
    zIndex: 2,
    alignSelf: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  zoomHelperNotLarge: {
    top: 8,
  },
  zoomHelperLarge: {
    top: 16,
  },
  zoomHelperText: {
    color: Colors.white,
    fontSize: 14,
    fontFamily: 'roboto-regular',
    fontWeight: '500',
  },
});

const MAP_AREA_THRESHOLD_KM2 = 30;

/**
 * Calculate the approximate viewable area of the map in km²
 * Uses the Haversine formula approximation for small areas
 */
function calculateMapAreaKm2(region) {
  const actualRegion = region.asNativeRegion();
  if (!actualRegion.latitudeDelta || !actualRegion.longitudeDelta || !actualRegion.latitude) {
    return 0;
  }

  const EARTH_RADIUS_KM = 6371;

  // Convert latitude degrees to radians
  const latRad = (actualRegion.latitude * Math.PI) / 180;

  // Calculate km per degree at this latitude
  const kmPerDegreeLat = (Math.PI / 180) * EARTH_RADIUS_KM;
  const kmPerDegreeLon = kmPerDegreeLat * Math.cos(latRad);

  // Calculate width and height in km
  const heightKm = actualRegion.latitudeDelta * kmPerDegreeLat;
  const widthKm = actualRegion.longitudeDelta * kmPerDegreeLon;

  return heightKm * widthKm;
}

function PantryMap(props) {
  const {
    actions,
    dataSource,
    navigation,
    registration,
    selectPantry,
    selectedPantryId,
    setSelectedPantryId,
    targetZipCode,
    translation,
  } = props;

  const MapView = props.MapView || MapViewImpl;
  // Nested component exposed via props primarily to facilitate testing by injection (but can be extended to support
  // initialization by contract, rather than implementation)

  const { user } = registration;

  const mapViewRef = useRef(null);

  // declare our constants
  const [displayPantry, setDisplayPantry] = useState({});
  const [region, setRegion] = useState(() => makeMapViewRegion(pantryRegionForUser(user)));
  const [mapToggle, setMapToggle] = useState(false);

  const getIcon = (pantry) => getMapMarkerIcon(pantry, selectedPantryId);

  const isLoggedIn = !!user?.id;
  // When retrieving (provider locations) via the API, we must signal whether or not a session is needed to satisfy the
  // request.  The resulting side-effect is the inclusion of the x-uidh header (session ID) in the request.  Atlhough we
  // don't technically NEED a session to retrieve location data for the map, we do need to send the session ID if one is
  // available, _so that_ the neighbour can be identified and any location visbility restrictions can be enforced.

  useEffect(() => {
    log('PantryMap.useEffect(centre): running');
    (async () => {
      const centeredRegion = await centerMap(targetZipCode);
      log('PantryMap.useEffect(centre): async centring', { centered: !!centeredRegion });
      if (centeredRegion) {
        setRegion(centeredRegion);
      }
    })();
  }, []);

  useEffect(() => {
    // Only animate for region changes we initiate (initial location, centering on a pin). A region
    // that came back from onRegionChangeComplete reflects where the map has *already* moved, so
    // re-animating to it is redundant and causes gesture lag on iOS. The decoration `fromOnChangeComplete`
    // will be undefined (falsey) unless explicitly set by `onRegionChangeComplete`
    if (!region.fromOnChangeComplete) {
      log('PantryMap.useEffect(animate)');
      mapViewRef.current?.animateToRegion(region);
    }
  }, [region.identity]);

  useEffect(() => {
    // Fetch the pantries defined by the initial view; note that this this action touches the global state, causing
    // the component to be re-initialized.
    log('PantryMap.useEffect(getPantries)');
    const nativeRegion = region.asNativeRegion();
    actions.getPantries(null, isLoggedIn, nativeRegion);
  }, [region.identity, isLoggedIn]);

  useEffect(() => {
    if (selectedPantryId) {
      const selectedPantry = dataSource.filter((p) => p.id == selectedPantryId)[0];
      if (selectedPantry) {
        log('PantryMap.useEffect(selectedPantry)', { selectedPantryId: selectedPantry.id });
        setRegion(region.center(selectedPantry.latitude, selectedPantry.longitude));
      }
    }
  }, [selectedPantryId]);

  useEffect(() => {
    // if location permission is denied,
    // hook to center on zipcode after zipcode has loaded
    (async () => {
      if (registration?.user?.zip_code) {
        const centeredRegion = await centerMap();
        if (centeredRegion) {
          setRegion(centeredRegion);
        }
      }
    })();
  }, [registration?.user?.zip_code]);

  // ---

  const handleMapCentering = async () => {
    const currentRegion = await centerMap();
    if (currentRegion) {
      setRegion(currentRegion);
      setMapToggle((v) => !v);
    } else {
      alert(translation.location_services_no_access);
    }
  };

  const centerMap = async (targetZip) => {
    let currentLocation;
    if (targetZip) {
      currentLocation = await getZipCodeLocation(targetZip);
      log('centerMap by targetZip', { hasLocation: !!currentLocation });
    } else {
      currentLocation = await getUserOrZipcodeLocation(registration?.user?.zip_code);
      log('centerMap by user ZIP', { hasLocation: !!currentLocation });
    }

    if (currentLocation) {
      // use default map zoom level
      currentLocation.coords.latitudeDelta = 0.225;
      currentLocation.coords.longitudeDelta = 0.308;
      actions.setLocation(currentLocation, true);

      return makeMapViewRegion(currentLocation.coords);
    }
  };

  const showPantryDetails = (e, pantry) => {
    setSelectedPantryId(pantry.id);
    logAnalyticsEvent('pantry_preview', {
      organizationId: pantry?.id,
    });

    // for mobile apps this is needed
    if (e.stopPropagation) e.stopPropagation();
    if (Layout.usePantryTabs) {
      setDisplayPantry(pantry);
      navigation.navigate('pantries', { id: pantry.id });
    } else {
      navigation.navigate('pantries', { id: pantry.id });
    }
  };

  const hidePantryDetails = () => {
    setDisplayPantry({});
    setSelectedPantryId(null);
    navigation.navigate('pantries');
  };

  const handleMapPress = (event) => {
    Keyboard.dismiss();
    // Dismiss keyboard when tapping map (important for iOS, that doesn't otherwise have a way to dimiss the soft
    // keyboard).

    hidePantryDetails(event);
  };

  const natigateToPantry = (pantryId) => {
    navigation.navigate('Pantry', { id: pantryId });
  };

  const onRegionChangeComplete = (newRegion) => {
    // Decorate a copy (not the instance the MapView handed us) so the animate effect knows the map has
    // already settled here and won't re-animate to it (see useEffect(animate) above).
    const settledRegion = clone(newRegion);
    settledRegion.fromOnChangeComplete = true;
    const defaultRegion = makeMapViewRegion(pantryRegionForUser(user));
    const isDefaultRegion = settledRegion.identity == defaultRegion.identity;
    if (!isDefaultRegion) {
      if (settledRegion?.latitude && settledRegion?.longitude) {
        setRegion(settledRegion);
      }
    } else {
      log('PantryMap.onRegionChangeComplete: ignoring callback for default region');
    }
  };

  const markers = [];
  const markerRefs = {};
  if (dataSource && dataSource.length) {
    dataSource.map((pantry) => {
      markers.push(
        <LazyMemoizedPantryMarker
          key={`Marker-${pantry.id}`}
          markerRefs={markerRefs}
          pantry={pantry}
          selectedPantryId={selectedPantryId}
          showPantryDetails={showPantryDetails}
          selectPantry={selectPantry}
          getIcon={getIcon}
        />
      );
    });
  }

  const dynamicStyles = StyleSheet.create({
    mapStyle: {
      width: Dimensions.get('window').width,
      height: Dimensions.get('window').height - 100,
    },
    locationCross: {
      bottom: displayPantry?.id ? 124 : 36,
    },
  });

  let pantryDetails = null;

  if (displayPantry?.id) {
    const selectPantry = (_e) => {
      if (markerRefs[displayPantry.id]) {
        markerRefs[displayPantry.id].hideCallout();
      }
      natigateToPantry(displayPantry.id);
    };
    let nextService = null;
    if (displayPantry.service_hours && displayPantry.service_hours.length) {
      nextService = `${translation.next_service_on}  ${timeUtils.getBriefServiceRow(
        displayPantry.service_hours[0]
      )}`;
    }
    pantryDetails = (
      <TouchableOpacity
        style={styles.pantryDetailsContainer}
        onPress={selectPantry}
        activeOpacity={0.8}
        testID="pantry-preview-window"
      >
        <PantryPhoto url={displayPantry.small_photo_url || displayPantry.avatar} />
        <View style={styles.pantryDetailsTextContainer}>
          <AppText style={styles.pantryDetailsName}>{displayPantry.name}</AppText>
          {typeof displayPantry.distance === 'number' ? (
            <PantryDistance distance={displayPantry.distance} style={styles.pantryDistance} />
          ) : null}
          {nextService ? <AppText style={styles.nextService}>{nextService}</AppText> : null}
          <AppText style={styles.link}>{translation.view_pantry}</AppText>
        </View>
      </TouchableOpacity>
    );
  }

  let locationCross = (
    <TouchableOpacity onPress={handleMapCentering}>
      <Image
        style={[styles.locationCross, dynamicStyles.locationCross]}
        source={require('../../assets/images/location_cross.png')}
      />
    </TouchableOpacity>
  );

  const mapAreaKm2 = calculateMapAreaKm2(region);
  const showZoomHelper = mapAreaKm2 > MAP_AREA_THRESHOLD_KM2;
  const zoomHelperText = showZoomHelper ? (
    <View
      style={[
        styles.zoomHelperContainer,
        Layout.isLargeDevice ? styles.zoomHelperLarge : styles.zoomHelperNotLarge,
      ]}
    >
      <AppText style={styles.zoomHelperText}>{translation.map_zoom_to_see_all}</AppText>
    </View>
  ) : null;

  return (
    <View style={styles.container}>
      {zoomHelperText}
      <MapView
        key={mapToggle}
        style={styles.mapStyle}
        initialRegion={Object.assign({}, region)}
        mapViewRef={mapViewRef}
        onRegionChangeComplete={onRegionChangeComplete}
        onPress={handleMapPress}
      >
        {markers}
      </MapView>
      {locationCross}
      {pantryDetails}
    </View>
  );
}

const getUserCurrentLocation = async () => {
  try {
    const { status } = await Location.requestForegroundPermissionsAsync();

    if (status == 'granted') {
      const location = await Location.getCurrentPositionAsync({
        enableHighAccuracy: true,
        accuracy: Location.Accuracy.High,
      });
      return location;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

const getZipCodeLocation = async (zipCode) => {
  if (zipCode) {
    const ZIP_CODE_GEOCODE_STRING = `${zipCode} zip code address`;

    try {
      const result = await geocode(ZIP_CODE_GEOCODE_STRING);
      const zipCodeLocation = {
        coords: {
          latitude: result.lat,
          longitude: result.lng,
        },
      };

      return zipCodeLocation;
    } catch (err) {
      return null;
    }
  }

  return null;
};

const getUserOrZipcodeLocation = async (zipCode) => {
  // get user's current location
  // if denied, get user's zipcode location

  const currentlocation = await getUserCurrentLocation();
  if (currentlocation) {
    return currentlocation;
  }

  const zipCodeLocation = await getZipCodeLocation(zipCode);

  return zipCodeLocation;
};

PantryMap.propTypes = {
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  dataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
  navigation: PropTypes.objectOf(PropTypes.any),
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  updatedAt: PropTypes.number,
  selectPantry: PropTypes.func,
  selectedPantryId: PropTypes.number,
  targetZipCode: PropTypes.string,
};

export default PantryMap;
